import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { of, ReplaySubject } from 'rxjs';
import { catchError, map, switchMap, takeUntil } from 'rxjs/operators';
import { TASK_PRIORITY } from '../../../core/helpers/constants';
import { createGuid } from '../../../core/helpers/utility';
import { IClient } from '../../../core/models/clients.models';
import { ITask } from '../../../core/models/tasks.models';
import * as moment from 'moment'
import { TasksApiService } from '../../../pages/tasks/tasks.apiservice';
import { EmailRequestModel, FormsApiService } from '../forms.apiservice';
import * as _ from 'underscore'
import { Utility } from '../../../shared/utility';
import Swal from 'sweetalert2';
import { DataFeedService } from '../../../shared/api/datafeed.service';
import { IUser } from '../../../core/models/datafeed.models';
import { AppService } from '../../../store/app.service';
import { UsersApiService } from '../../../pages/users/users.apiservice';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'modal-strategy-review-form',
  templateUrl: './strategy-review-form.component.html',
  styleUrls: ['./strategy-review-form.component.scss']
})

export class StrategyReviewFormComponent implements OnInit, OnDestroy {

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  @Output() oSaveClicked = new EventEmitter<any>();

  public TASK_PRIORITY = TASK_PRIORITY;

  addTaskForm!: FormGroup;
  submitted = false;
  allUsers: Array<IUser> = new Array<IUser>();

  private requestorName: string = '';
  private requestorEmail: string = '';

  @Input() set iClientId(value: any) {
    if (value !== null)
      this.addTaskForm.controls['clientId'].patchValue(value);
  }
  @Input() set iCampaignId(value: any) {
    if (value !== null)
      this.addTaskForm.controls['campaignId'].patchValue(value);
  }
  @Input() set iUserId(value: any) {
    if (value !== null)
      this.addTaskForm.controls['assignedUserId'].patchValue(value);
  }
  @Input() iClientName: string = '';
  @Input() iCampaignName: string = '';
  @Input() iDmsName: string = '';

  id: number = -1;

  private getDefaultDueDate(): string {
    const due = moment().add(3, 'days').utc().hour(17).minute(0).second(0).millisecond(0);
    const day = due.day();
    if (day === 6) {
      due.add(2, 'days');
    } else if (day === 0) {
      due.add(1, 'days');
    }
    return due.format('YYYY-MM-DD[T]HH:mm:ss.SSS[Z]');
  }

  constructor(
    private taskService: TasksApiService,
    public activeModal: NgbActiveModal,
    private formBuilder: FormBuilder,
    private dataFeedService: DataFeedService,
    private formsApiService: FormsApiService,
    private appService: AppService,
    private usersApiService: UsersApiService,
    private translate: TranslateService) {
    this.addTaskForm = this.formBuilder.group({
      id: [null],
      test: [null],
      title: ['Strategy Review'],
      priority: [2, [Validators.required]],
      campaignId: [null],
      clientId: [null],
      dueDate: [this.getDefaultDueDate(), Validators.required],
      description: [''],
      assignedUserId: ['', [Validators.required]],
      isCompleted: [null]
    });
    this.dataFeedService.getAllTaskAssignees()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: any) => {
        if (response.good) {
          this.allUsers = [...response.data].map((i) => { i.fullName = i.firstName + ' ' + i.lastName; return i; });

          this.applyDefaultAssignee();
        }
      });
  }

  private applyDefaultAssignee(): void {
    const control = this.addTaskForm?.controls?.['assignedUserId'];
    if (!control) return;

    const currentValue = control.value;
    if (currentValue !== null && currentValue !== undefined && String(currentValue).trim() !== '') {
      return;
    }

    const assignee = this.allUsers.find(u =>
      (u?.firstName ?? '').trim().toLowerCase() === 'whitney' &&
      (u?.lastName ?? '').trim().toLowerCase() === 'bowles'
    );

    if (assignee?.id) {
      control.patchValue(assignee.id);
    }
  }

  get f() { return this.addTaskForm.controls;}

  updateData(data: IClient) {
    this.addTaskForm.patchValue(data);
  }

  ngOnInit() {
    const userId = this.appService.getActiveUser$().value?.id || '';
    if (userId) {
      this.usersApiService.getUserDetails(userId)
        .pipe(takeUntil(this.destroyed$))
        .subscribe((response: any) => {
          if (response?.data) {
            this.requestorName = `${response.data.firstName ?? ''} ${response.data.lastName ?? ''}`.trim();
            this.requestorEmail = String(response.data.email ?? '').trim();
          }
        });
    }
  }

  private escapeHtml(value: unknown): string {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private buildHtmlBodyFromPlain(plainText: unknown): string {
    const escaped = this.escapeHtml(plainText);
    return `<div style="white-space:pre-wrap">${escaped}</div>`;
  }

  private buildEmailBody(task: ITask): string {
    const safe = (v: unknown) => {
      const s = String(v ?? '').trim();
      return s.length ? s : '—';
    };

    const formatDueDate = (value: unknown): string => {
      const raw = String(value ?? '').trim();
      if (!raw) return '—';
      const parsed = moment(raw);
      return parsed.isValid() ? parsed.format('DD MMM YYYY') : safe(value);
    };

    const lines: string[] = [];
    lines.push(this.translate.instant('STRATEGY_REVIEW_EMAIL_BODY_HEADER'));
    lines.push('');
    lines.push(`${this.translate.instant('REQUESTED_BY')}: ${safe(this.requestorName)}`);
    lines.push(`${this.translate.instant('DUE_DATE')}: ${formatDueDate(task?.dueDate)}`);
    if (this.iCampaignName) lines.push(`${this.translate.instant('CAMPAIGN')}: ${safe(this.iCampaignName)}`);
    if (this.iClientName) lines.push(`${this.translate.instant('CLIENT')}: ${safe(this.iClientName)}`);
    lines.push('');
    lines.push(safe(task?.description));
    return lines.join('\n');
  }

  private resolveAssigneeEmail(assignedUserId: unknown) {
    const id = String(assignedUserId ?? '').trim();
    if (!id) return of('');

    const fromList = (this.allUsers ?? []).find(u => String((u as any)?.id ?? '').trim() === id) as any;
    const listEmail = String(fromList?.email ?? '').trim();
    if (listEmail) return of(listEmail);

    // Fallback: task assignee list may not include email in some environments.
    return this.usersApiService.getUserDetails(id).pipe(
      map((resp: any) => String(resp?.data?.email ?? '').trim()),
      catchError(() => of(''))
    );
  }


  onSubmit() {
    this.submitted = true;

    if (this.addTaskForm.invalid) {
      console.warn('Form is invalid:', this.addTaskForm.errors);
      return;
    }
    if (this.f.id.value === null || this.f.id.value === -1)
      this.f.id.setValue(createGuid())

    if (this.f.campaignId.value === -1)
      this.f.campaignId.setValue(null);
    if (this.f.clientId.value === -1)
      this.f.clientId.setValue(null);

    let formValues: ITask = this.addTaskForm.value;

    formValues = this.trimObjectStrings(formValues);

    const prefix = this.translate.instant('STRATEGY_REVIEW_REQUEST_PREFIX');
    const rawDetails = String(formValues.description ?? '').trim();
    const descriptionParts: string[] = [prefix];
    const dmsName = String(this.iDmsName ?? '').trim();
    if (dmsName.length) {
      descriptionParts.push(`${this.translate.instant('DMS')}: ${dmsName}`);
    }
    if (rawDetails.length) {
      descriptionParts.push(this.translate.instant('DETAILS'), rawDetails);
    }
    formValues.description = descriptionParts.join('\n');

    formValues.dueDate = Utility.getTaskDueDateFromDateTime(formValues.dueDate);

    const emailBody = this.buildEmailBody(formValues);
    const subjectSuffix = this.iCampaignName ? ` - ${this.iCampaignName}` : (this.iClientName ? ` - ${this.iClientName}` : '');

    this.resolveAssigneeEmail(formValues?.assignedUserId)
      .pipe(
        takeUntil(this.destroyed$),
        switchMap((toEmail: string) => {
          const emailRequest: EmailRequestModel | null = toEmail
            ? {
                ToEmail: toEmail,
                Subject: `${this.translate.instant('NEW_STRATEGY_REVIEW_REQUEST')}${subjectSuffix}`,
                ReplyToEmail: this.requestorEmail,
                PlainContent: emailBody,
                HtmlContent: this.buildHtmlBodyFromPlain(emailBody),
                CcEmails: '',
                BccEmails: '',
                FromName: ''
              }
            : null;

          if (!emailRequest) {
            console.warn('No assignee email found; task will be created without sending an email.', {
              assignedUserId: formValues?.assignedUserId,
              assignee: (this.allUsers ?? []).find((u: any) => String(u?.id ?? '').trim() === String(formValues?.assignedUserId ?? '').trim())
            });
          }

          return this.taskService.saveTask(formValues).pipe(
            map((response: any) => ({ response, emailRequest }))
          );
        })
      )
      .subscribe(({ response, emailRequest }: { response: any; emailRequest: EmailRequestModel | null }) => {
        if (response && response.good) {
          if (emailRequest) {
            this.formsApiService.sendEmail(emailRequest).pipe(takeUntil(this.destroyed$)).subscribe({
              next: () => {},
              error: (emailError) => {
                console.error('Error sending email:', emailError);
              }
            });
          }
          this.activeModal.close('saved');
          Swal.fire({
            icon: 'success',
            title: this.translate.instant('THANK_YOU'),
            text: this.translate.instant('STRATEGY_REVIEW_SUCCESS_TEXT'),
            confirmButtonText: this.translate.instant('OK')
          });
        } else {
          Swal.fire({
            icon: 'error',
            title: this.translate.instant('ERROR'),
            text: this.translate.instant('STRATEGY_REVIEW_ERROR_TEXT'),
            confirmButtonText: this.translate.instant('OK')
          });
        }
      });
  }

  // This function will recursively iterate through the object
  trimObjectStrings(obj: any): any {
    // Iterate over each property of the object
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const value = obj[key];
        
        if (typeof value === 'string') {
          // Trim the string if the value is a string
          obj[key] = value.trim();
        } else if (typeof value === 'object' && value !== null) {
          // Recursively call the function if the value is an object (or array)
          this.trimObjectStrings(value);
        }
      }
    }
    return obj;
  }

  close() {
    this.activeModal.dismiss();
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

}
